import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useSiteSettings } from '@/settings/useSiteSettings';
import { getPublishedProductById } from '@/services/products';
import { getPlanById } from '@/services/plans';
import { getEnabledPaymentGateways, createCheckoutSession } from '@/services/paymentGateways';
import type { Product, ProductPlan, PaymentGatewayConfig, PaymentGatewayCode } from '@/domain/types';

const gatewayMeta: Record<string, { icon: string; color: string; desc: string }> = {
  stripe:    { icon: 'ri-bank-card-2-line', color: '#635bff', desc: 'Credit card, debit card, or digital wallet' },
  paypal:    { icon: 'ri-paypal-line', color: '#003087', desc: 'PayPal account or credit card' },
  '2checkout': { icon: 'ri-secure-payment-line', color: '#1a1a2e', desc: 'Secure global payment via Verifone' },
  manual:    { icon: 'ri-hand-coin-line', color: '#6366f1', desc: 'Bank transfer or manual payment' },
  crypto:    { icon: 'ri-funds-line', color: '#6c5ce7', desc: 'NOWPayments — Bitcoin, Ethereum, USDC, BNB & more' },
  paddle:    { icon: 'ri-wallet-3-line', color: '#86d166', desc: 'Pay via Paddle checkout' },
  sslcommerz: { icon: 'ri-shield-check-line', color: '#e2136e', desc: 'Pay via SSLCommerz' },
};

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-surface-200 rounded-lg ${className}`} />;
}

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { settings } = useSiteSettings();

  const productId = searchParams.get('productId');
  const planId = searchParams.get('planId');

  const [product, setProduct] = useState<Product | null>(null);
  const [plan, setPlan] = useState<ProductPlan | null>(null);
  const [gateways, setGateways] = useState<PaymentGatewayConfig[]>([]);
  const [selected, setSelected] = useState<PaymentGatewayCode | ''>('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!productId || !planId) { navigate('/products'); return; }
    async function load() {
      try {
        const [prod, pl] = await Promise.all([
          getPublishedProductById(productId),
          getPlanById(planId),
        ]);
        if (!prod || !pl) { setError('Product or plan not found.'); return; }
        setProduct(prod); setPlan(pl);
        const gws = getEnabledPaymentGateways(settings.payments.enabledGateways);
        setGateways(gws);
        if (gws.length > 0) setSelected(gws[0].code);
      } catch { setError('Failed to load checkout details.'); }
      finally { setLoading(false); }
    }
    load();
  }, [productId, planId, navigate, settings]);

  useEffect(() => {
    if (!authLoading && !user && !loading) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
  }, [user, authLoading, loading, navigate]);

  const handleCheckout = async () => {
    if (!product || !plan || !selected) return;
    setProcessing(true); setError('');
    try {
      const session = await createCheckoutSession({
        userId: user!.id,
        productId: product.id,
        planId: plan.id,
        gatewayCode: selected,
        successUrl: `${window.location.origin}/dashboard/orders?success=true`,
        cancelUrl: window.location.href,
      });
      if (session.redirectUrl) {
        window.location.href = session.redirectUrl;
      } else {
        navigate('/dashboard/orders');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed.');
      setProcessing(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface-50 to-surface-100 pt-20 pb-16">
        <div className="px-4 md:px-6 max-w-4xl mx-auto">
          <Skeleton className="h-9 w-48 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3"><Skeleton className="h-40 w-full" /></div>
            <div className="lg:col-span-2"><Skeleton className="h-80 w-full" /></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface-50 to-surface-100 pt-20 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
            <i className="ri-error-warning-line text-3xl text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-surface-900 mb-2">Something went wrong</h2>
          <p className="text-surface-500 mb-6">{error}</p>
          <Link to="/products" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-brand-600 text-white font-semibold text-sm hover:bg-brand-700 transition-colors">
            <i className="ri-arrow-left-line" /> Back to Products
          </Link>
        </div>
      </div>
    );
  }

  if (!product || !plan) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-50 to-surface-100 pt-20 pb-16">
      <div className="px-4 md:px-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-2 text-sm text-surface-400 mb-6">
          <Link to="/products" className="hover:text-brand-600 transition-colors">Products</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-surface-600 font-medium">{product.name}</span>
          <i className="ri-arrow-right-s-line" />
          <span className="text-surface-800 font-semibold">Checkout</span>
        </div>

        {error && (
          <div className="mb-6 flex items-center gap-2.5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            <i className="ri-error-warning-line shrink-0" /> {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: Payment Method + Account */}
          <div className="lg:col-span-3 space-y-5">
            {/* Account */}
            <div className="bg-white rounded-2xl border border-surface-200 p-6 shadow-sm">
              <h2 className="text-sm font-bold text-surface-900 mb-4 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
                  <i className="ri-user-line text-brand-600 text-sm" />
                </div>
                Account
              </h2>
              {user ? (
                <div className="flex items-center gap-3.5 p-3.5 rounded-xl bg-surface-50 border border-surface-100">
                  <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold text-sm shrink-0">
                    {user.email?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-surface-900 truncate">{user.email}</p>
                    <p className="text-xs text-green-600 flex items-center gap-1.5 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> Signed in
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                  <span className="font-semibold">Sign in required.</span> Please{' '}
                  <Link to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                    className="underline font-medium hover:text-amber-900">sign in</Link> to continue.
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-2xl border border-surface-200 p-6 shadow-sm">
              <h2 className="text-sm font-bold text-surface-900 mb-4 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
                  <i className="ri-bank-card-line text-brand-600 text-sm" />
                </div>
                Payment Method
              </h2>
              <div className="space-y-3">
                {gateways.map((gw) => {
                  const meta = gatewayMeta[gw.code] || { icon: 'ri-bank-card-line', color: '#6366f1', desc: '' };
                  const isOn = selected === gw.code;
                  return (
                    <button key={gw.id} type="button" onClick={() => setSelected(gw.code)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                        isOn ? 'border-brand-500 bg-brand-50/50 shadow-sm' : 'border-surface-200 bg-white hover:border-brand-300 hover:shadow-sm'
                      }`}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{ backgroundColor: meta.color + '15', color: meta.color }}>
                        <i className={`${meta.icon} text-lg`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-surface-900">{gw.name}</p>
                          {isOn && <span className="px-1.5 py-0.5 rounded-full bg-brand-100 text-brand-700 text-[10px] font-semibold">Selected</span>}
                        </div>
                          <p className="text-xs text-surface-400 mt-0.5">{gw.code === 'manual' && settings.payments.manualContactLink ? `Contact: ${settings.payments.manualContactLink}` : meta.desc}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                        isOn ? 'border-brand-500 bg-brand-500' : 'border-surface-300'
                      }`}>
                        {isOn && <i className="ri-check-line text-white text-xs font-bold" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Payment method icons when Stripe is selected */}
              {selected === 'stripe' && (
                <div className="mt-4 pt-4 border-t border-surface-100">
                  <p className="text-xs font-medium text-surface-500 mb-3">Accepted payment methods</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Visa', icon: 'ri-visa-line' },
                      { label: 'MC', icon: 'ri-mastercard-line' },
                      { label: 'Amex', icon: 'ri-bank-card-2-line' },
                      { label: 'Apple Pay', icon: 'ri-apple-fill' },
                      { label: 'Google Pay', icon: 'ri-google-fill' },
                      { label: 'Klarna', icon: 'ri-secure-payment-line' },
                      { label: 'Link', icon: 'ri-link' },
                      { label: 'iDEAL', icon: 'ri-bank-line' },
                    ].map((pm) => (
                      <div key={pm.label} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface-50 border border-surface-200 text-xs text-surface-600 font-medium">
                        <i className={`${pm.icon} text-xs`} />
                        {pm.label}
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-surface-400 mt-2">Actual methods depend on your Stripe account settings and customer location.</p>
                </div>
              )}

              {/* Payment method icons when Crypto is selected */}
              {selected === 'crypto' && (
                <div className="mt-4 pt-4 border-t border-surface-100">
                  <p className="text-xs font-medium text-surface-500 mb-3">Accepted cryptocurrencies</p>
                  <div className="flex flex-wrap gap-2">
                    {(settings.payments.nowpayments.acceptedCurrencies || 'BTC,ETH,USDT,BNB')
                      .split(',')
                      .map((c) => c.trim())
                      .filter(Boolean)
                      .map((coin) => {
                        const meta: Record<string, { label: string; icon: string }> = {
                          BTC: { label: 'Bitcoin', icon: 'ri-currency-line' },
                          ETH: { label: 'Ethereum', icon: 'ri-eth-line' },
                          USDT: { label: 'USDT', icon: 'ri-money-dollar-circle-line' },
                          USDTBSC: { label: 'USDT (BSC)', icon: 'ri-money-dollar-circle-line' },
                          USDC: { label: 'USDC', icon: 'ri-coins-line' },
                          BNB: { label: 'BNB', icon: 'ri-funds-line' },
                          DOGE: { label: 'DOGE', icon: 'ri-money-dollar-circle-line' },
                          SOL: { label: 'SOL', icon: 'ri-flashlight-line' },
                          XRP: { label: 'XRP', icon: 'ri-shuffle-line' },
                          ADA: { label: 'ADA', icon: 'ri-hashtag' },
                          TRX: { label: 'TRX', icon: 'ri-flashlight-line' },
                          MATIC: { label: 'MATIC', icon: 'ri-hexagon-line' },
                          LTC: { label: 'Litecoin', icon: 'ri-currency-line' },
                          DASH: { label: 'Dash', icon: 'ri-flashlight-line' },
                          ATOM: { label: 'Cosmos', icon: 'ri-planet-line' },
                          DOT: { label: 'Polkadot', icon: 'ri-link' },
                          AVAX: { label: 'Avalanche', icon: 'ri-flashlight-line' },
                          BCH: { label: 'Bitcoin Cash', icon: 'ri-currency-line' },
                          FIL: { label: 'Filecoin', icon: 'ri-database-2-line' },
                          NEAR: { label: 'NEAR', icon: 'ri-planet-line' },
                          APT: { label: 'Aptos', icon: 'ri-hexagon-line' },
                          ARB: { label: 'Arbitrum', icon: 'ri-hexagon-line' },
                          OP: { label: 'Optimism', icon: 'ri-flashlight-line' },
                        };
                        const m = meta[coin.toUpperCase()] || { label: coin, icon: 'ri-currency-line' };
                        return (
                          <div key={coin} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface-50 border border-surface-200 text-xs text-surface-600 font-medium">
                            <i className={`${m.icon} text-xs`} />
                            {m.label}
                          </div>
                        );
                      })}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-surface-400">
                    <span>Powered by <strong>NOWPayments</strong></span>
                    <span>Default: <strong>{settings.payments.nowpayments.defaultPayCurrency || 'USDTBSC'}</strong></span>
                  </div>
                  <p className="text-[10px] text-surface-400 mt-1">You'll be redirected to complete payment. Order activates automatically on confirmation.</p>
                </div>
              )}
            </div>
          </div>

          {/* Right: Summary + Purchase */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-surface-200 shadow-sm sticky top-24 overflow-hidden">
              <div className="px-6 py-4 border-b border-surface-100">
                <h2 className="text-sm font-bold text-surface-900">Order Summary</h2>
              </div>

              <div className="px-6 py-5 flex gap-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-surface-100 border border-surface-100 shrink-0">
                  {product.thumbnail ? (
                    <img src={product.thumbnail} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><i className="ri-box-3-line text-2xl text-surface-300" /></div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-surface-900 truncate">{product.name}</p>
                  <p className="text-xs text-surface-500">{plan.name}</p>
                  <p className="text-xs text-surface-400 mt-1">{plan.siteLimit >= 999 ? 'Unlimited sites' : `Up to ${plan.siteLimit} sites`}</p>
                </div>
              </div>

              <div className="px-6 pb-2 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-surface-500">Subtotal</span>
                  <span className="text-surface-900 font-medium">${plan.price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-surface-500">Tax</span>
                  <span className="text-surface-900 font-medium">$0.00</span>
                </div>
              </div>

              <div className="mx-6 py-4 border-t border-surface-100 flex justify-between items-center">
                <span className="text-sm font-bold text-surface-900">Total</span>
                <span className="text-lg font-bold text-brand-600">${plan.price.toFixed(2)} USD</span>
              </div>

              <div className="px-6 pb-6 space-y-3">
                <button onClick={handleCheckout}
                  disabled={processing || !selected || !user}
                  className="w-full py-3 rounded-xl bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-sm">
                  {processing ? <><i className="ri-loader-4-line animate-spin" /> Processing…</> : <><i className="ri-lock-2-line" /> Complete Purchase</>}
                </button>

                <p className="text-xs text-surface-400 text-center leading-relaxed">
                  You'll be redirected to complete payment after placing your order.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
